/**
 * Описание настроек: какого типа значение и в каких пределах.
 *
 * До этого одна настройка описывалась в трёх местах: тип в
 * `CommunitySettings`, проверка значения в репозитории и разбор запроса в
 * маршруте API. Забыть любое из них ничего не ломало на глаз, а
 * `SettingsRepository` отвергает всё, чего нет в проверке, и роняет
 * сохранение целиком — так однажды настройки перестали сохраняться вовсе
 * из-за номера организации.
 *
 * Теперь описание одно, а тип `Record<keyof CommunitySettings, ...>` не даёт
 * добавить настройку и не описать её: этого не пропустит компилятор.
 *
 * Подписи полей и подсказки живут в интерфейсе, а не здесь: у поля рядом с
 * соседями свои пояснения, и общая форма «по описанию» вышла бы беднее
 * написанной руками. Здесь только `label` — для сообщений об ошибке, чтобы
 * человек читал «Размер состава», а не `defaultCapacity`.
 */

import { isMessageSettings } from './messages.js';
import { isNotificationSettings } from './notifications.js';
import { EVENT_MENTIONS, MEMBER_LISTS, type CommunitySettings } from './settings.js';
import { isValidTimeZone } from './time.js';

interface SpecBase {
  /** Как поле называется в сообщениях об ошибке. */
  label: string;
  /** Допустимо ли `null` как полноправное значение, а не «не задано». */
  nullable?: boolean;
}

export type SettingsFieldSpec = SpecBase &
  (
    | { kind: 'boolean' }
    | { kind: 'integer'; min: number; max: number }
    | { kind: 'text'; maxLength: number }
    | { kind: 'timezone' }
    /** Выбор одного из нескольких заранее известных значений. */
    | { kind: 'choice'; options: readonly string[] }
    /** Список строк, например типы мероприятий. */
    | { kind: 'stringList'; minItems: number; maxItems: number; maxLength: number; uppercase?: boolean }
    /** Набор личных сообщений — у него своя структура и свой редактор. */
    | { kind: 'notifications' }
    /** Набор текстов в каналах — тоже со своей структурой и редактором. */
    | { kind: 'messages' }
  );

/**
 * Пределы подобраны так, чтобы ничего из уже сохранённого не отвергнуть.
 *
 * Проверка применяется и при чтении: значение, которое ей не подошло, молча
 * заменяется умолчанием. Поэтому ужесточить правило — значит незаметно
 * сбросить сообществу его настройку, и пределы здесь заведомо просторнее
 * того, что встречается на деле.
 */
export const SETTINGS_SCHEMA: Readonly<Record<keyof CommunitySettings, SettingsFieldSpec>> = {
  timezone: { kind: 'timezone', label: 'Часовой пояс' },
  eventTypes: {
    kind: 'stringList',
    label: 'Типы мероприятий',
    minItems: 1,
    maxItems: 25,
    maxLength: 100,
    uppercase: true,
  },
  defaultCapacity: { kind: 'integer', label: 'Размер состава по умолчанию', min: 1, max: 100 },
  maxVisibleRows: { kind: 'integer', label: 'Строк участников в сообщении', min: 1, max: 500 },
  autoCloseOnStart: { kind: 'boolean', label: 'Закрывать набор при начале мероприятия' },
  eventJoinTarget: { kind: 'choice', label: 'Куда попадает плюс', options: MEMBER_LISTS },
  eventAnnounceMention: { kind: 'choice', label: 'Кого зовёт анонс', options: EVENT_MENTIONS },
  eventAnnounceLeftMain: { kind: 'boolean', label: 'Объявлять уход из основы' },
  eventAnnounceLeftReserve: { kind: 'boolean', label: 'Объявлять уход из замены' },
  eventReopenOnTimeChange: { kind: 'boolean', label: 'Открывать заново при переносе' },
  reminderMinutesBefore: { kind: 'integer', label: 'За сколько минут напоминать', min: 1, max: 1440 },
  voiceCheckEnabled: { kind: 'boolean', label: 'Проверять сбор в голосовом перед началом' },
  voiceCheckMinutesBefore: { kind: 'integer', label: 'За сколько минут проверять сбор', min: 1, max: 120 },
  familyReapplyDays: { kind: 'integer', label: 'Пауза после отказа, дней', min: 0, max: 365 },
  familyMinAge: { kind: 'integer', label: 'Минимальный возраст в заявке', min: 1, max: 99 },
  familyMaxAge: { kind: 'integer', label: 'Максимальный возраст в заявке', min: 1, max: 99 },
  familyAdequacyMax: { kind: 'integer', label: 'Верх шкалы адекватности', min: 2, max: 10 },
  familyRequireClipLink: { kind: 'boolean', label: 'Требовать ссылку в поле «Откаты»' },
  welcomeMessageEnabled: { kind: 'boolean', label: 'Приветствие новичку в канале' },
  newcomerRoleEnabled: { kind: 'boolean', label: 'Выдавать роль новичка' },
  rollbackMaxAttempts: { kind: 'integer', label: 'Попыток разослать откаты', min: 1, max: 100 },
  /*
   * Организацию бот определяет сам, но номер можно задать руками — и стереть,
   * чтобы он определил заново. Отсюда `null` как полноправное значение.
   */
  combatFamilyId: {
    kind: 'integer',
    label: 'Организация в Majestic',
    min: 1,
    max: Number.MAX_SAFE_INTEGER,
    nullable: true,
  },
  /*
   * Название приходит из игровой службы, а не от человека. Предел здесь
   * только против мусора: если бы он отвергал настоящее название, фоновая
   * сверка каптов падала бы на попытке его сохранить.
   */
  combatFamilyName: { kind: 'text', label: 'Название организации', maxLength: 500, nullable: true },
  notifications: { kind: 'notifications', label: 'Личные сообщения' },
  messages: { kind: 'messages', label: 'Тексты в Discord' },
};

const SETTINGS_KEYS = Object.keys(SETTINGS_SCHEMA) as (keyof CommunitySettings)[];

/** Все настройки по порядку описания. */
export function settingsKeys(): (keyof CommunitySettings)[] {
  return [...SETTINGS_KEYS];
}

/** Подходит ли значение своему описанию. */
export function isValidSettingValue(spec: SettingsFieldSpec, value: unknown): boolean {
  if (value === null) {
    return spec.nullable === true;
  }

  switch (spec.kind) {
    case 'boolean':
      return typeof value === 'boolean';

    case 'integer':
      return typeof value === 'number' && Number.isInteger(value) && value >= spec.min && value <= spec.max;

    case 'text':
      return typeof value === 'string' && value.length <= spec.maxLength;

    case 'timezone':
      return typeof value === 'string' && isValidTimeZone(value);

    case 'choice':
      return typeof value === 'string' && spec.options.includes(value);

    case 'stringList':
      return (
        Array.isArray(value) &&
        value.length >= spec.minItems &&
        value.length <= spec.maxItems &&
        value.every((item) => typeof item === 'string' && item.trim() !== '' && item.length <= spec.maxLength)
      );

    case 'notifications':
      return isNotificationSettings(value);

    case 'messages':
      return isMessageSettings(value);
  }
}

/** Проверка значения настройки по её ключу. */
export function isAssignableSetting(key: keyof CommunitySettings, value: unknown): boolean {
  return isValidSettingValue(SETTINGS_SCHEMA[key], value);
}

/** Человеческое описание допустимых значений — для сообщений об ошибке. */
export function describeSettingSpec(spec: SettingsFieldSpec): string {
  const tail = spec.nullable === true ? ' либо пустым' : '';

  switch (spec.kind) {
    case 'boolean':
      return `должно быть да/нет${tail}`;
    case 'integer':
      return `должно быть целым числом от ${String(spec.min)} до ${String(spec.max)}${tail}`;
    case 'text':
      return `должно быть текстом не длиннее ${String(spec.maxLength)} символов${tail}`;
    case 'timezone':
      return 'должно быть часовым поясом IANA, например Europe/Moscow';
    case 'choice':
      return `должно быть одним из: ${spec.options.join(', ')}`;
    case 'stringList':
      return `должно быть списком из ${String(spec.minItems)}–${String(spec.maxItems)} значений`;
    case 'notifications':
      return 'должно быть набором личных сообщений';
    case 'messages':
      return 'должно быть набором текстов';
  }
}
